import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAdminAuth } from "@/contexts/AdminAuthContext";

export default function FranchiseStaffPage() {
  const { admin } = useAdminAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.franchiseStaffList().then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  // Only real franchise owners see "add staff". Sub-admins and Admins get read-only.
  const canManage = admin?.role === "franchise";

  const save = async () => {
    setBusy(true);
    try {
      const placeholder = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      await api.franchiseStaffCreate({ ...form, password: placeholder });
      toast.success("Sub-admin invited — invite email sent");
      setOpen(false);
      setForm({ name: "", email: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };
  const toggle = async (r) => {
    try {
      await api.franchiseStaffUpdate(r.id, { is_active: !r.is_active });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold font-heading">Sub-admins</h1>
          <p className="text-sm text-muted-foreground">Team members you delegate to for {admin?.assigned_city || "your city"}.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="franchise-staff-add">Add sub-admin</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>New sub-admin</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Input placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                <p className="text-xs text-muted-foreground">A one-time set-password link is emailed to your sub-admin. City is inherited from your account — sub-admins can only act on {admin?.assigned_city}.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} disabled={busy || !form.email}>{busy ? "Sending…" : "Send invite"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>
      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
            <tr><th className="py-3 px-4 text-left">Name</th><th className="py-3 px-4 text-left">Email</th><th className="py-3 px-4">Status</th><th className="py-3 px-4">Created</th><th className="py-3 px-4 text-right">Actions</th></tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td className="py-10 text-center text-muted-foreground" colSpan={5}>No sub-admins yet.</td></tr>
            ) : rows.map((r) => (
              <tr key={r.id} className="border-t border-border" data-testid={`staff-row-${r.id}`}>
                <td className="py-3 px-4 font-medium">{r.name}</td>
                <td className="py-3 px-4 text-muted-foreground">{r.email}</td>
                <td className="py-3 px-4 text-center"><Badge variant={r.is_active ? "default" : "secondary"}>{r.is_active ? "Active" : "Suspended"}</Badge></td>
                <td className="py-3 px-4 text-center text-muted-foreground">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</td>
                <td className="py-3 px-4 text-right">
                  {canManage && <Button size="sm" variant="outline" onClick={() => toggle(r)}>{r.is_active ? "Suspend" : "Reactivate"}</Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
